import Multipoint from '@arcgis/core/geometry/Multipoint';
import Point from '@arcgis/core/geometry/Point';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import MapView from '@arcgis/core/views/MapView';
import {nearestVertex, crosses, distance } from '@arcgis/core/geometry/geometryEngine';
import Polyline from '@arcgis/core/geometry/Polyline';
import Graphic from '@arcgis/core/Graphic';
import { SurfaceBreakline } from '../model/SurfaceBreakline';
import { SurfaceDef } from '../model/SurfaceDef';
import { GroundPoint } from '../model/GroundPoint';
import { MapViewInputTask } from '../framework/MapViewInputTask';
import {validNextPointSymbol, invalidNextLineSymbol, validNextLineSymbol, resultLineSymbol, invalidNextPointSymbol } from '../resources/esriSymbols';

export class NewBreaklineInputTask extends MapViewInputTask<SurfaceBreakline> {
  private readonly surfaceDef: SurfaceDef;

  //setup state vars
  private redoStack: GroundPoint[] = [];
  private breaklineResult = new SurfaceBreakline();
  private pointerMovePoint?: Point;

  private feedbackLayer = new GraphicsLayer({ title: 'feedbackLayer', listMode: 'hide' });
  private resultLayer = new GraphicsLayer({ title: 'resultLayer', listMode: 'hide' });

  private allGroundPoints: GroundPoint[] = [];
  private allGroundPointsMP: Multipoint = new Multipoint();
  private filterGroundPointsDesc:string[] =[];

  constructor(mapView: MapView, surfaceDef: SurfaceDef) {
    super(mapView);
    this.surfaceDef = surfaceDef;
  }

  //overridable lifecycle methods
  protected onSetup() {
    super.onSetup();

    this.redoStack = [];
    this.breaklineResult = new SurfaceBreakline();
    this.pointerMovePoint = undefined;

    this.allGroundPoints = this.surfaceDef.getAllGroundPoints();
    const multipoint: Multipoint = new Multipoint({ spatialReference: this.mapView.spatialReference });
    for (const point of this.allGroundPoints) {
      multipoint.addPoint(point.generateGeometry(this.mapView.spatialReference));
    }
    this.allGroundPointsMP = multipoint;

    this.cursor = 'crosshair';
    this.mapView.map.add(this.feedbackLayer);
    this.mapView.map.add(this.resultLayer);
  }
  protected onCleanup() {
    super.onCleanup();

    this.mapView.map.remove(this.feedbackLayer);
    this.mapView.map.remove(this.resultLayer);
    this.feedbackLayer.removeAll();
    this.resultLayer.removeAll();

    this.redoStack = [];
    this.breaklineResult = new SurfaceBreakline();
    this.pointerMovePoint = undefined;

    this.allGroundPoints = [];
    this.allGroundPointsMP = new Multipoint();
  }
  protected onPointerMove(e: __esri.ViewPointerMoveEvent): void {
    super.onPointerMove(e);

    this.pointerMovePoint = this.mapView.toMap(e);
    this.updateFeedbackLayer();
  }
  protected onImmediateClick(e: __esri.ViewImmediateClickEvent) {
    super.onImmediateClick(e);

    // find the next GroundPoint
    const groundPoint = this.findNearestGroundPoint(e.mapPoint);

    //validate input
    const valid = this.validateNextPoint(groundPoint);
    if (!valid) {
      // ignore user input
      return; //short circuit
    }

    //if valid, add the point
    this.breaklineResult.groundPoints.push(groundPoint);

    // clear the redoStack, it is now invalidated
    this.redoStack.splice(0, this.redoStack.length);

    this.updateResultLayer();
    this.updateFeedbackLayer();
  }
  protected onImmediateDoubleClick(e: __esri.ViewImmediateDoubleClickEvent) {
    super.onImmediateDoubleClick(e);

    e.stopPropagation();
    // cleanup();
    // resolvePromise(breaklineResult);

    if (this.breaklineResult.groundPoints.length >= 2) {
      this.completeNow(this.breaklineResult);
    } else {
      this.cancelNow();
    }
  }
  protected onKeyDown(e: __esri.ViewKeyDownEvent) {
    super.onKeyDown(e);

    if (e.key === 'Enter') {
      e.stopPropagation();
      if (this.breaklineResult.groundPoints.length >= 2) {
        this.completeNow(this.breaklineResult);
      } else {
        this.cancelNow();
      }
      return;
    }

    if (e.key === 'z' || e.key === 'Z') {
      // undo last digitized point
      e.stopPropagation();
      if (this.breaklineResult.groundPoints.length > 0) {
        this.redoStack.push(this.breaklineResult.groundPoints.pop()!);
        this.updateResultLayer();
        this.updateFeedbackLayer();
      }
      return;
    }
    if (e.key === 'r' || e.key === 'R') {
      // redo last digitized point
      e.stopPropagation();
      if (this.redoStack.length > 0) {
        this.breaklineResult.groundPoints.push(this.redoStack.pop()!);
        this.updateResultLayer();
        this.updateFeedbackLayer();
      }
      return;
    }
  }

  private findNearestGroundPoint(mapPoint: Point): GroundPoint {
    const findResult = nearestVertex(this.allGroundPointsMP, mapPoint);
    const result = this.allGroundPoints[findResult.vertexIndex];
    return result;
  }

  private validateNextPoint(groundPoint: GroundPoint): boolean {
    // detect non-eligible point
    if (!groundPoint.visible) return false;
    if (!groundPoint.tinable) return false;

    // detect duplicate point
    if (this.breaklineResult.groundPoints.indexOf(groundPoint) >= 0) return false;

    // detect crossings
    if (this.breaklineResult.groundPoints.length > 0) {
      const lastPoint = this.breaklineResult.groundPoints[this.breaklineResult.groundPoints.length - 1];
      const nextPoint = groundPoint;
      const nextLine = new Polyline();
      nextLine.spatialReference = this.mapView.spatialReference;
      nextLine.hasZ = true;
      nextLine.addPath([
        lastPoint.generateGeometry(this.mapView.spatialReference),
        nextPoint.generateGeometry(this.mapView.spatialReference),
      ]);

      if (this.breaklineResult.groundPoints.length > 1) {
        const otherLine = this.breaklineResult.generateGeometry(this.mapView.spatialReference);
        const crossess = crosses(nextLine, otherLine);
        if (crossess) return false;
      }
      for (const breakline of this.surfaceDef.breaklines) {
        const otherLine = breakline.generateGeometry(this.mapView.spatialReference);
        const crossess = crosses(nextLine, otherLine);
        if (crossess) return false;
      }
    }

    return true;
  }

  private updateFeedbackLayer(): void {
    //clear the layer
    this.feedbackLayer.removeAll();

    if (!this.pointerMovePoint) {
      return;
    }

    //construct the nextPoint graphic
    const groundPoint = this.findNearestGroundPoint(this.pointerMovePoint);
    const valid = this.validateNextPoint(groundPoint);

    const nextPoint = groundPoint.generateGeometry(this.mapView.spatialReference);
    const nextPointGraphic = new Graphic({
      geometry: nextPoint,
      symbol: valid ? validNextPointSymbol : invalidNextPointSymbol,
    });
    this.feedbackLayer.add(nextPointGraphic);

    if (this.breaklineResult.groundPoints.length > 0) {
      //construct the nextLine graphic
      const lastGroundPoint = this.breaklineResult.groundPoints[this.breaklineResult.groundPoints.length - 1];
      const lastPoint = lastGroundPoint.generateGeometry(this.mapView.spatialReference);
      const nextLine = new Polyline();
      nextLine.spatialReference = this.mapView.spatialReference;
      nextLine.hasZ = true;
      nextLine.addPath([lastPoint, valid ? nextPoint : this.pointerMovePoint]);

      const nextLineGraphic = new Graphic();
      nextLineGraphic.geometry = nextLine;
      nextLineGraphic.symbol = valid ? validNextLineSymbol : invalidNextLineSymbol;
      this.feedbackLayer.add(nextLineGraphic);
    }
  }

  private updateResultLayer() {
    //clear the layer
    this.resultLayer.removeAll();

    //reconstruct the line graphic
    if (this.breaklineResult.groundPoints.length > 1) {
      const polyline = this.breaklineResult.generateGeometry(this.mapView.spatialReference);

      const graphic = new Graphic();
      graphic.geometry = polyline;
      graphic.symbol = resultLineSymbol;
      this.resultLayer.add(graphic);
    }
  }

  public setUpGenerateBreakLine(description: string, isClosed: boolean, breaklinesLayer: GraphicsLayer): any {
    this.breaklineResult = new SurfaceBreakline();
    this.breaklineResult.groundPoints.splice(0);

    const messages: string[] = [];

    //Get all points with the given description
    const allPoints = this.surfaceDef.getAllGroundPoints().filter(point => point.description === description);

    //Filter unique points based on minimum distance constraint**
    const isBreaklineLongEnough = this.isBreaklineLongEnough(allPoints);

    if (!isBreaklineLongEnough) {
      //breakline < 1 foot and will not display.
      messages.push('Warning:Breakline_6')
      return { breakline: null, messages};
    }

    // Sort the filtered points based on their "name" property
    allPoints.sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
    );

    // Add sorted points to the breakline result
    this.breaklineResult.groundPoints.push(...allPoints);

       // 🔁 Check for self-intersection (loop)
    const hasLoop = this.doesBreaklineHaveLoops();
    if (hasLoop) {
      messages.push('Error:Breakline_5'); // custom code for self-intersection/loop
      return { breakline: null, messages };
    }


    // **Check if we should close the breakline**
    if (isClosed && this.breaklineResult.groundPoints.length > 2) {
      const canClose: boolean = this.attemptCloseBreakline();
      if (!canClose) {
        messages.push('Error:Breakline_5')
      }
    }

    return { breakline: this.breaklineResult, messages };
  }

  private isBreaklineLongEnough(filteredPoints: GroundPoint[]): boolean {
    if (filteredPoints.length < 2) {
      return false; // Not enough points to generate a breakline
    }

    const minBreakLineDistance = 1; // Minimum breakline distance
    let totalLength = 0;

    for (let i = 0; i < filteredPoints.length - 1; i++) {
      const segmentStart = filteredPoints[i];
      const segmentEnd = filteredPoints[i + 1];

      // Calculate distance between consecutive points
      const segmentLength = distance(
        segmentStart.generateGeometry(this.mapView.spatialReference),
        segmentEnd.generateGeometry(this.mapView.spatialReference)
      );

      totalLength += segmentLength;

      // check if total length exceeds 1 foot, then return.
      if (totalLength >= minBreakLineDistance) {
        return true;
      }
    }

    return false; // total length never reaches 1 foot.
  }

  private attemptCloseBreakline() {
    const firstPoint = this.breaklineResult.groundPoints[0];
    const lastPoint = this.breaklineResult.groundPoints[this.breaklineResult.groundPoints.length - 1];

    // Create geometry for the closing segment
    const closingSegment = new Polyline({
      spatialReference: this.mapView.spatialReference,
      hasZ: true,
      paths: [[[lastPoint.x, lastPoint.y, lastPoint.z], [firstPoint.x, firstPoint.y, firstPoint.z]]]
    });

    // Check if the closing segment intersects any existing segment
    let canClose = true;
    for (let i = 0; i < this.breaklineResult.groundPoints.length - 1; i++) {
      const segmentStart = this.breaklineResult.groundPoints[i];
      const segmentEnd = this.breaklineResult.groundPoints[i + 1];

      const segmentPolyline = new Polyline({
        spatialReference: this.mapView.spatialReference,
        hasZ: true,
        paths: [[[segmentStart.x, segmentStart.y, segmentStart.z], [segmentEnd.x, segmentEnd.y, segmentEnd.z]]]
      });

      if (crosses(closingSegment, segmentPolyline)) {
        canClose = false;
        break;
      }
    }

    if (canClose) {
      this.breaklineResult.groundPoints.push(firstPoint);
    }
    return canClose;
  }

  private doesBreaklineHaveLoops(): boolean {
    const points = this.breaklineResult.groundPoints;
    const sr = this.mapView.spatialReference;
  
    for (let i = 0; i < points.length - 1; i++) {
      const segmentA = new Polyline({
        spatialReference: sr,
        hasZ: true,
        paths: [[[points[i].x, points[i].y, points[i].z], [points[i + 1].x, points[i + 1].y, points[i + 1].z]]]
      });
  
      for (let j = i + 2; j < points.length - 1; j++) {
        // skip adjacent segments
        if (i === 0 && j === points.length - 2) continue;
  
        const segmentB = new Polyline({
          spatialReference: sr,
          hasZ: true,
          paths: [[[points[j].x, points[j].y, points[j].z], [points[j + 1].x, points[j + 1].y, points[j + 1].z]]]
        });
  
        if (crosses(segmentA, segmentB)) {
          return true; // found intersection
        }
      }
    }
    return false;
  } 

}
